/**
 * Add Source Modal Component
 * Handles source registration forms for all source types
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { 
  registerDatabaseSource,
  registerLocalFolderSource,
  registerSharePointSource,
  registerGDriveSource,
} from '../../api/sources';
import { uploadArtifact } from '../../api/artifacts';
import { 
  Database, 
  Plus, 
  X,
  Folder,
  Cloud,
  HardDrive,
  File,
  Globe,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Eye,
  EyeOff,
  Clock,
  CheckCircle,
} from 'lucide-react';
import { LocalFolderForm } from './LocalFolderForm';
import { SharePointForm } from './SharePointForm';
import { GDriveForm } from './GDriveForm';
import { WebsiteForm } from './WebsiteForm';

interface AddSourceModalProps {
  projectId: string;
  sourceType: 'database' | 'local_folder' | 'sharepoint' | 'gdrive' | 'website';
  onSourceTypeChange: (type: 'database' | 'local_folder' | 'sharepoint' | 'gdrive' | 'website') => void;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddSourceModal({ projectId, sourceType, onSourceTypeChange, onClose, onSuccess }: AddSourceModalProps) {
  
  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 -top-[30px] left-0 flex items-center justify-center z-50" onClick={onClose}>
      <div className="max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <Card className="dark:bg-slate-800 dark:border-slate-700">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Register New Source</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Source Type Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Source Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <button
                  onClick={() => onSourceTypeChange('database')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'database'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                  }`}
                >
                  <Database className="h-6 w-6 mb-2 text-blue-600" />
                  <div className="font-semibold text-theme-text">Database</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">PostgreSQL, MySQL, MS SQL</div>
                </button>
                
                <button
                  onClick={() => onSourceTypeChange('local_folder')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'local_folder'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                  }`}
                >
                  <Folder className="h-6 w-6 mb-2 text-green-600" />
                  <div className="font-semibold text-theme-text">Local Folder</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">File system directory</div>
                </button>
                
                <button
                  onClick={() => onSourceTypeChange('sharepoint')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'sharepoint'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                  }`}
                >
                  <Cloud className="h-6 w-6 mb-2 text-purple-600" />
                  <div className="font-semibold text-theme-text">SharePoint</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Microsoft SharePoint</div>
                </button>
                
                <button
                  onClick={() => onSourceTypeChange('gdrive')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'gdrive'
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                  }`}
                >
                  <Cloud className="h-6 w-6 mb-2 text-red-600" />
                  <div className="font-semibold text-theme-text">Google Drive</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Google Drive</div>
                </button>

                <button
                  onClick={() => onSourceTypeChange('website')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sourceType === 'website'
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                  }`}
                >
                  <Globe className="h-6 w-6 mb-2 text-teal-600" />
                  <div className="font-semibold text-theme-text">Website</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">URLs & web pages</div>
                </button>
              </div>
            </div>
            
            {/* Form Content */}
            {sourceType === 'local_folder' && (
              <LocalFolderForm
                projectId={projectId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            )}
            
            {sourceType === 'sharepoint' && (
              <SharePointForm
                projectId={projectId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            )}
            
            {sourceType === 'gdrive' && (
              <GDriveForm
                projectId={projectId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            )}

            {sourceType === 'website' && (
              <WebsiteForm
                projectId={projectId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            )}
            
            {sourceType === 'database' && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-900 dark:text-blue-300">
                  Database registration requires detailed connection configuration. Please use the API directly or a notebook script. See API docs for schema.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

